<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="help-flyout-wrapper">
    <StandardButton
      v-if="showHelpButton"
      @click="() => openFlyout()"
      :aria-label="$bct('Help')"
      class="help-flyout-trigger"
      color="primary"
      icon="help_outline"
      icon-size="20"
      size="small"
      variant="flat"
    />
    <ContentDialog
      @close="onDialogClose"
      ref="helpDialogRef"
      :max-width="900"
      title="Help"
    >
      <template #content>
        <v-row
          v-if="showSearchBar"
          align="center"
          class="help-flyout-search mb-5"
          no-gutters
        >
          <LabeledTextfield
            v-model="searchQuery"
            @focus="viewedSearchResult = null"
            @keydown.enter.prevent="runSearch"
            :aria-label="$bct('Search documentation')"
            :placeholder="$bct('Search documentation')"
            class="help-flyout-search-input"
            cols="10"
            variant="outlined"
            clearable
            dense
          />
          <v-col cols="auto">
            <StandardButton
              @click="runSearch"
              :loading="searchLoading"
              :text="$bct('Search')"
              class="help-flyout-search-btn"
              color="primary"
              prepend-icon="search"
            />
          </v-col>
        </v-row>
        <div class="help-flyout-content overflow-y-auto">
          <template
            v-if="
              viewedSearchResult &&
              sectionContents[viewedSearchResult.path || viewedSearchResult.id]
            "
          >
            <div
              v-html="sectionContents[viewedSearchResult.path || viewedSearchResult.id]"
              class="help-flyout-section-content markdown-body"
            />
          </template>
          <template v-else-if="hasSearched">
            <v-list
              v-if="searchResults.length > 0"
              density="compact"
            >
              <v-list-item
                v-for="item in searchResults"
                @click="openSection(item)"
                :key="item.id"
                link
              >
                <v-list-item-title>{{ item.title }}</v-list-item-title>
                <v-list-item-subtitle
                  v-if="item.snippet"
                  class="help-flyout-search-snippet"
                >
                  <span v-html="item.snippet" />
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
            <div
              v-else
              class="text-medium-emphasis py-4"
            >
              {{ $bct('No results found.') }}
            </div>
          </template>
          <template v-else>
            <template v-if="contextualSections.length > 0">
              <div
                v-for="(section, idx) in contextualSections"
                :key="section.id"
                :class="{ 'mt-6': idx > 0 }"
                class="help-flyout-section"
              >
                <div
                  v-if="sectionContents[section.path || section.id]"
                  v-html="sectionContents[section.path || section.id]"
                  class="help-flyout-section-content markdown-body"
                />
              </div>
            </template>
            <div
              v-else
              class="text-medium-emphasis py-4"
            >
              <template v-if="documentationRouteName">
                {{ $bct('No help content for this page. Search for help content or ') }}
                <a
                  class="text-primary text-decoration-none"
                  href="/documentation"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {{ $bct('view all help documentation') }}
                </a>
              </template>
              <template v-else>
                {{ $bct('No help content for this page. Use search above to find help.') }}
              </template>
            </div>
          </template>
        </div>
        <div class="text-medium-emphasis pt-4 mt-4 border-t">
          {{ $bct('Need help? You can contact support from our help desk. ') }}
          <a
            class="text-primary text-decoration-none"
            href="https://bridgecare.atlassian.net/servicedesk/customer/portal/1"
            rel="noopener noreferrer"
            target="_blank"
          >
            {{ $bct('File a support ticket') }}
          </a>
        </div>
      </template>
    </ContentDialog>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';

import ContentDialog from '@/shared/components/ContentDialog.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import StandardButton from '@/standard/StandardButton.vue';
import useApi from '@/shared/composables/useApi';

const $bct = inject('translator');
const route = useRoute();
const router = useRouter();
const { api } = useApi();

const props = defineProps({
  documentationRouteName: {
    type: String,
    default: '',
  },
  showHelpButton: {
    type: Boolean,
    default: true,
  },
});

const helpDialogRef = ref(null);
const flyoutOpen = ref(false);
const hasSearched = ref(false);
const searchLoading = ref(false);
const showSearchBar = ref(true);
const searchQuery = ref('');
const searchResults = ref([]);
const contextualSections = ref([]);
const sectionContents = ref({});
const viewedSearchResult = ref(null);

const routePage = computed(() => {
  const meta = route.meta;
  return meta?.page ?? null;
});

const DOCUMENTATION_VIEW_ROOTS = ['admin', 'bridgecare', 'specialist', 'parent', 'provider'];

function documentationViewFromPageKey(page) {
  const prefix = (page || '').split('/')[0]?.toLowerCase();
  return DOCUMENTATION_VIEW_ROOTS.includes(prefix) ? prefix : undefined;
}

async function loadContextualSections() {
  if (!routePage.value) {
    contextualSections.value = [];
    return;
  }
  const page = routePage.value;
  const params = { page };
  const viewFromPage = documentationViewFromPageKey(page);
  if (viewFromPage) {
    params.view = viewFromPage;
  }
  const res = await api.documentation.index(params);
  contextualSections.value = res?.data ?? [];
}

async function loadSectionContent(pathOrId) {
  if (!pathOrId || sectionContents.value[pathOrId]) return;
  const params = {};
  const pathView = (pathOrId || '').split('/')[0];
  if (
    ['parent', 'provider'].includes(pathView) &&
    route.query.locale &&
    route.query.locale !== 'en'
  ) {
    params.locale = route.query.locale;
  }
  const res = await api.documentation.get(pathOrId, params);
  sectionContents.value = { ...sectionContents.value, [pathOrId]: res?.data?.html ?? '' };
}

const documentationLinkQuery = computed(() => {
  const view = route.query.view;
  if (!view || !['admin', 'bridgecare', 'parent', 'provider', 'specialist'].includes(view)) {
    return undefined;
  }
  if (view === 'specialist') return undefined;
  const q = { view };
  if (['parent', 'provider'].includes(view) && route.query.locale && route.query.locale !== 'en') {
    q.locale = route.query.locale;
  }
  return q;
});

async function openSection(item) {
  const isInDocumentationCenter =
    route.path.startsWith('/documentation') && props.documentationRouteName;
  const path = item.path || item.id;

  if (isInDocumentationCenter) {
    helpDialogRef.value?.close();
    await router.push({
      name: props.documentationRouteName,
      params: { pathMatch: path },
      query: documentationLinkQuery.value,
    });
    return;
  }

  viewedSearchResult.value = item;
  await loadSectionContent(path);
}

async function runSearch() {
  const trimmed = (searchQuery.value || '').trim();
  if (!trimmed) {
    hasSearched.value = false;
    searchResults.value = [];
    return;
  }
  hasSearched.value = true;
  searchLoading.value = true;
  const params = { q: trimmed };
  if (route.path.startsWith('/documentation') && route.query.view) {
    params.view = route.query.view;
    if (
      ['parent', 'provider'].includes(route.query.view) &&
      route.query.locale &&
      route.query.locale !== 'en'
    ) {
      params.locale = route.query.locale;
    }
  }
  const res = await api.documentation.index(params);
  const results = res?.data ?? [];
  searchResults.value = results;

  window.analytics?.track?.('Documentation Search', {
    search_query: trimmed,
    result_count: results.length,
    results: results.map((item) => ({
      id: item.id ?? null,
      title: item.title ?? null,
      path: item.path ?? null,
    })),
    organization_id: window.organization_id,
    organization_name: window.organization_name,
    window_url: window.location.href,
    window_pathname: window.location.pathname,
    window_search: window.location.search,
  });

  searchLoading.value = false;
}

watch(searchQuery, (val) => {
  if (!(val || '').trim()) hasSearched.value = false;
});

watch(
  contextualSections,
  (sections) => {
    sections.forEach((s) => loadSectionContent(s.path || s.id));
  },
  { immediate: true, deep: true },
);

watch(routePage, async () => {
  if (flyoutOpen.value && searchQuery.value.trim().length === 0) {
    await loadContextualSections();
  }
});

function onDialogClose() {
  flyoutOpen.value = false;
  showSearchBar.value = true;
}

async function openFlyout(initialDocument = null) {
  searchQuery.value = '';
  hasSearched.value = false;
  searchResults.value = [];
  viewedSearchResult.value = null;
  sectionContents.value = {};
  flyoutOpen.value = true;
  helpDialogRef.value?.open();

  if (initialDocument) {
    showSearchBar.value = false;
    const id = initialDocument.id ? `help-document-${initialDocument.id}` : initialDocument.id;
    sectionContents.value = { [id]: initialDocument.content || '' };
    viewedSearchResult.value = { id, title: initialDocument.title || '' };
  } else {
    showSearchBar.value = true;
    await loadContextualSections();
  }
}

defineExpose({ openFlyout });
</script>

<style scoped>
.help-flyout-search-input :deep(.v-field) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.help-flyout-search-btn {
  border-radius: 0;
  min-height: 40px !important;
}

.help-flyout-wrapper {
  position: relative;
}

.help-flyout-trigger {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 999;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.help-flyout-content {
  max-height: 60vh;
}

.help-flyout-section-content :deep(h1) {
  font-size: 1.25rem;
  margin-top: 0;
  margin-bottom: 0.75rem;
}

.help-flyout-section-content :deep(h2) {
  font-size: 1.1rem;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
}

.help-flyout-section-content :deep(h3) {
  font-size: 1rem;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.help-flyout-section-content :deep(p),
.help-flyout-section-content :deep(ul),
.help-flyout-section-content :deep(ol) {
  margin-top: 0;
  margin-bottom: 0.75rem;
}

.help-flyout-section-content :deep(code) {
  font-size: 0.85em;
}

.help-flyout-section-content :deep(pre) {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  padding: 0.75rem;
  margin: 0.5rem 0;
  overflow-x: auto;
}

.help-flyout-section-content :deep(img) {
  max-width: 100%;
  height: auto;
}

.help-flyout-search-snippet {
  white-space: normal;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 0.75rem;
}

.help-flyout-search-snippet :deep(mark) {
  background-color: rgba(var(--v-theme-primary), 0.2);
  padding: 0 0.1em;
  border-radius: 2px;
}
</style>
